import { useEffect, useState } from "react";
import { MapPin, ArrowUpRight, Clock, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLandingConfig } from "@/hooks/useLandingConfig";
import SafeImg from "@/components/landing/SafeImg";

const DEFAULT_MONOPOLI_PHOTO = "https://thb.tildacdn.net/tild6466-3233-4934-a362-623737366364/-/resize/1200x/IMG_4202_1.jpg";
const DEFAULT_POLIGNANO_PHOTO = "https://thb.tildacdn.net/tild6565-6336-4534-a236-373532663434/-/resize/1200x/DSC06065.jpg";

// Sedi + "Porta il tuo metodo nella tua città" (era Franchising, ora accorpato).
// Testi editabili via /admin/landing-config.
export default function LandingLocations() {
  const { T } = useLandingConfig();
  const [sediDb, setSediDb] = useState([]);

  useEffect(() => {
    // /api/sedi è pubblico (senza chiavi sensibili). Ci serve `cover_image_url`
    // per usare la foto caricata dall'admin di sede come fallback quando
    // il super_admin non ha impostato un'immagine dedicata nel CMS Landing.
    api.get("/sedi").then(r => setSediDb(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const findSedeByName = (needle) => {
    const n = needle.toLowerCase();
    return sediDb.find(s => (s.name || "").toLowerCase().includes(n));
  };
  const sedeMonopoli = findSedeByName("monopoli");
  const sedePolignano = findSedeByName("polignano");

  const eyebrow = T("texts.locations.eyebrow", "Dove ci trovi");
  const t1 = T("texts.locations.title_line1", "Le nostre");
  const t2 = T("texts.locations.title_line2", "sedi.");
  const italicWord = T("texts.locations.title_italic_word", "sedi");
  const franchisingTitle = T("texts.locations.franchising_title", "Porta il tuo metodo nella tua città");
  const franchisingSubtitle = T("texts.locations.franchising_subtitle", "Contattaci per aprire un Epic Flow con il pacchetto completo di metodo, allestimento e supporto operativo.");
  const franchisingCta = T("texts.locations.franchising_cta_label", "Scopri come");
  const franchisingHref = T("cta_links.locations_franchising_href", "") || T("cta_links.sedi_franchising_href", "/franchising");
  // Cascade foto Monopoli: CMS dedicata → CMS generica → cover_image_url da DB sede
  const photoMonopoli =
    T("images.sede_monopoli_photo", "") ||
    T("images.locations_photo", "") ||
    sedeMonopoli?.cover_image_url ||
    "";
  // Cascade foto Polignano: CMS dedicata → cover_image_url da DB sede
  const photoPolignano =
    T("images.sede_polignano_photo", "") ||
    sedePolignano?.cover_image_url ||
    "";
  const monopoliBadge = T("texts.locations.monopoli_badge", "Studio attivo");
  const monopoliName = T("texts.locations.monopoli_name", "Monopoli");
  const monopoliAddress = T("texts.locations.monopoli_address", "Via Luigi Indelli 91A · BA");
  const monopoliMapsUrl = T("cta_links.location_monopoli_maps", "https://maps.google.com/?q=Via+Luigi+Indelli+91A+Monopoli");
  const polignanoBadge = T("texts.locations.polignano_badge", "Coming soon · Q2 2026");
  const polignanoName = T("texts.locations.polignano_name", "Polignano a Mare");
  const polignanoAddress = T("texts.locations.polignano_address", "Il secondo studio Epic Flow");
  const polignanoMapsUrl = T("cta_links.location_polignano_maps", "");

  const renderTitleLine = (line) => {
    if (!italicWord || !line?.toLowerCase().includes(italicWord.toLowerCase())) return line;
    const idx = line.toLowerCase().indexOf(italicWord.toLowerCase());
    return (
      <>
        {line.slice(0, idx)}
        <span className="font-editorial text-mauve">{line.slice(idx, idx + italicWord.length)}</span>
        {line.slice(idx + italicWord.length)}
      </>
    );
  };

  return (
    <section data-testid="landing-locations" id="sedi" className="relative bg-plum-dark text-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="text-center mb-20 md:mb-24">
          <div className="text-[10px] uppercase tracking-[0.42em] text-cream/60 mb-8 flex items-center justify-center gap-3">
            <span className="w-10 h-px bg-cream/40" />
            {eyebrow}
            <span className="w-10 h-px bg-cream/40" />
          </div>
          <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-cream">
            {renderTitleLine(t1)}{" "}{italicWord ? renderTitleLine(t2) : <span className="font-editorial text-primary">{t2}</span>}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {/* Monopoli — attivo */}
          <a
            href={monopoliMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="location-monopoli"
            className="group relative block overflow-hidden rounded-[2rem]"
          >
            <div className="aspect-[3/4] overflow-hidden">
              <SafeImg
                src={photoMonopoli}
                fallback={DEFAULT_MONOPOLI_PHOTO}
                alt={`Sede ${monopoliName}`}
                className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-foreground/85" />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] mb-4 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground">
                <span className="w-2 h-2 rounded-full bg-current animate-pulse" />
                {monopoliBadge}
              </div>
              <div className="font-serif-display text-3xl md:text-4xl text-background leading-tight">{monopoliName}</div>
              <div className="flex items-center gap-2 mt-3 text-background/85 text-sm">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5}/>
                <span>{monopoliAddress}</span>
              </div>
            </div>
          </a>

          {/* Polignano — foto & testi editabili */}
          <div
            data-testid="location-polignano"
            className="group relative block overflow-hidden rounded-[2rem]"
          >
            <div className="aspect-[3/4] overflow-hidden">
              <SafeImg
                src={photoPolignano}
                fallback={DEFAULT_POLIGNANO_PHOTO}
                alt={`Sede ${polignanoName}`}
                className={`w-full h-full object-cover ${polignanoMapsUrl ? "transition-transform duration-[900ms] group-hover:scale-105" : "grayscale-[40%]"}`}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-foreground/85" />
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] mb-4 px-3 py-1.5 rounded-full bg-primary text-primary-foreground">
                <Clock className="w-3 h-3" strokeWidth={2}/>
                {polignanoBadge}
              </div>
              <div className="font-serif-display text-3xl md:text-4xl text-background leading-tight">{polignanoName}</div>
              <div className="flex items-center gap-2 mt-3 text-background/85 text-sm">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5}/>
                <span>{polignanoAddress}</span>
              </div>
            </div>
          </div>

          {/* Franchising — Porta il tuo metodo nella tua città */}
          <Link
            to={franchisingHref}
            data-testid="location-franchising"
            className="group relative block overflow-hidden rounded-[2rem] bg-secondary text-secondary-foreground"
          >
            <div className="aspect-[3/4] p-8 md:p-10 flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] mb-4 px-3 py-1.5 rounded-full bg-secondary-foreground/15">
                  <Sparkles className="w-3 h-3" strokeWidth={2}/>
                  Nuova apertura
                </div>
              </div>
              <div>
                <div className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.02] mb-4">
                  {franchisingTitle}
                </div>
                <p className="text-sm opacity-85 leading-relaxed mb-6 font-light max-w-xs">
                  {franchisingSubtitle}
                </p>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] group-hover:gap-3 transition-all">
                  {franchisingCta}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.5}/>
                </div>
              </div>
              {/* Decoro sottile */}
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-secondary-foreground/10 blur-3xl pointer-events-none" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
